/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { useTransition, animated } from '@react-spring/web';
import { DefaultEventsMap } from '@socket.io/component-emitter';
import { Circle, CircleStop, List, Sparkles } from 'lucide-react';
import { Dispatch, FC, useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { v4 as uuid } from 'uuid';
import uuidFromString from 'uuid-by-string';

import { useUserContext } from '../Root';

import { HoverEffect } from './HoverEffect/HoverEffect';
import { createSeparatorStep } from './LiveAgentSessionUtils';
import { actionToIcon } from './LiveAgentStepComponent';
import { actionProps, Step } from './LiveAgentTypes';

import Spinner from '@/components/ui/Spinner';
import { cn } from '@/utils';

export type StepKind = 'atomicAction' | 'aiPrompt' | 'recordedActions';
export const stepKinds = [
    { kind: 'atomicAction', label: 'Add steps', icon: List },
    { kind: 'aiPrompt', label: 'Prompt steps', icon: Sparkles },
    { kind: 'recordedActions', label: 'Show the agent', icon: Circle },
] as const;

const hoverEffectProps = {
    scale: 1.02,
    duration: 0.1,
    shadow: true,
    shadowColor: '000',
    shadowOpacity: 0.1,
    cardBackgroundColor: 'fff',
    modifier: 2,
};

export const LiveAgentRemoteControl: FC<{
    dispatch: Dispatch<any>;
    socket: Socket<DefaultEventsMap, DefaultEventsMap>;
    steps: Step[];
    isDisabled?: boolean;
    openBlocksModal?: () => void;
}> = ({ dispatch, socket, steps, isDisabled, openBlocksModal }) => {
    const { canOperate } = useUserContext().user;
    const [selectedStepKindIndex, setSelectedStepKindIndex] = useState<number>(0);
    const [isRecording, setIsRecording] = useState<boolean>(false);

    const handleCategoryClick = (index: number) => {
        setSelectedStepKindIndex(index);
    };

    const currentlyRunningRecorderStepIndex = steps.findIndex(
        (step) => step.kind === 'recordedActions' && step.recording,
    );
    const currentlyRunningRecorderStep = steps[currentlyRunningRecorderStepIndex];

    const handleRecordingStart = (target: string) => {
        socket.emit('startRecording', { fieldName: 'targetFieldName' });
        dispatch({
            type: 'add',
            payload: { kind: 'recordedActions', executed: true, intent: target, recording: true },
        });
        setIsRecording(true);
    };

    const handleRecordingStop = () => {
        if (currentlyRunningRecorderStep) {
            dispatch({
                type: 'update',
                payload: {
                    index: currentlyRunningRecorderStepIndex,
                    data: {
                        recording: false,
                    },
                },
            });
        }
        socket.emit('stopRecording', { fieldName: 'none' });
        setIsRecording(false);
    };

    const transitions = useTransition(selectedStepKindIndex, {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    });

    const filteredStepKinds = stepKinds.filter((step) => canOperate || step.kind !== 'recordedActions');

    return (
        <div className="px-8 bg-white mb-2 mx-auto max-w-[600px] w-full">
            <div className="rounded-lg p-2 border border-slate-300 shadow-bottom">
                <div
                    className={cn(
                        'flex flex-row items-center gap-x-2 border-b border-slate-300',
                        canOperate ? '' : 'justify-between',
                    )}
                >
                    {filteredStepKinds.map((step, index) => (
                        <button
                            key={index}
                            className={`flex items-center text-p-ui-medium gap-x-3 justify-center py-2 px-3 cursor-pointer border-b-2 -mb-px ${
                                selectedStepKindIndex === index ? 'border-purple-500' : 'border-transparent'
                            }`}
                            onClick={() => handleCategoryClick(index)}
                        >
                            <step.icon className="text-purple-600" />
                            <span>{step.label}</span>
                        </button>
                    ))}
                </div>

                <div className="grid">
                    {transitions((style, i) => (
                        <animated.div className="row-start-1 row-end-1 col-start-1 col-end-1 min-w-[0]" style={style}>
                            {filteredStepKinds[i].kind === 'atomicAction' ? (
                                <div className="flex mt-3 flex-wrap">
                                    <div className="flex-1 space-y-1">
                                        {(Object.keys(actionProps) as Array<keyof typeof actionProps>)
                                            .filter((key) => actionProps[key].type === 'single')
                                            .map((key) => {
                                                const action = actionProps[key];
                                                return (
                                                    <button
                                                        key={key}
                                                        className={cn(
                                                            'flex items-center gap-x-1 justify-center px-3 cursor-pointer rounded transition-bg duration-200 hover:bg-purple-100 text-left',
                                                        )}
                                                        onClick={() => {
                                                            dispatch({
                                                                type: 'add',
                                                                payload: action.step,
                                                            });
                                                        }}
                                                    >
                                                        <action.icon size="16px" />
                                                        <span className="text-subtle font-medium">{action.label}</span>
                                                    </button>
                                                );
                                            })}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        {(Object.keys(actionProps) as Array<keyof typeof actionProps>)
                                            .filter((key) => actionProps[key].type === 'assertion')
                                            .map((key) => {
                                                const action = actionProps[key];
                                                return (
                                                    <HoverEffect key={key} {...hoverEffectProps}>
                                                        <button
                                                            className={cn(
                                                                'flex items-center gap-x-1 justify-center p-2 cursor-pointer rounded-[6px] transition-bg duration-200 hover:bg-purple-100 w-full h-full border',
                                                            )}
                                                            onClick={() => {
                                                                dispatch({
                                                                    type: 'add',
                                                                    payload: action.step,
                                                                });
                                                            }}
                                                        >
                                                            <action.icon width="16px" height="28px" />
                                                            <span className="text-subtle font-medium">
                                                                {action.label}
                                                            </span>
                                                        </button>
                                                    </HoverEffect>
                                                );
                                            })}
                                        {openBlocksModal &&
                                            (Object.keys(actionProps) as Array<keyof typeof actionProps>)
                                                .filter((key) => actionProps[key].type === 'block')
                                                .map((key) => {
                                                    const action = actionProps[key];
                                                    return (
                                                        <HoverEffect key={key} {...hoverEffectProps}>
                                                            <button
                                                                className={cn(
                                                                    'flex items-center gap-x-1 justify-center p-2 cursor-pointer rounded-[6px] transition-bg duration-200 hover:bg-purple-100 w-full h-full border',
                                                                )}
                                                                onClick={openBlocksModal}
                                                            >
                                                                <action.icon width="16px" height="28px" />
                                                                <span className="text-subtle font-medium">
                                                                    {action.label}
                                                                </span>
                                                            </button>
                                                        </HoverEffect>
                                                    );
                                                })}
                                        {(Object.keys(actionProps) as Array<keyof typeof actionProps>)
                                            .filter((key) => actionProps[key].type === 'extra')
                                            .map((key) => {
                                                const action = actionProps[key];
                                                return (
                                                    <button
                                                        key={key}
                                                        className={cn(
                                                            'flex items-center gap-x-1 justify-center px-3 cursor-pointer rounded transition-bg duration-200 hover:bg-purple-100 text-left',
                                                        )}
                                                        onClick={() => {
                                                            dispatch({
                                                                type: 'add',
                                                                payload: action.step,
                                                            });
                                                        }}
                                                    >
                                                        <action.icon size="16px" />
                                                        <span className="text-subtle font-medium">{action.label}</span>
                                                    </button>
                                                );
                                            })}
                                    </div>
                                </div>
                            ) : null}
                            {filteredStepKinds[i].kind === 'aiPrompt' ? (
                                <AIPromptForm
                                    isDisabled={isDisabled}
                                    steps={steps}
                                    socket={socket}
                                    dispatch={dispatch}
                                />
                            ) : null}

                            {filteredStepKinds[i].kind === 'recordedActions' ? (
                                <RecorderForm
                                    isRecording={isRecording}
                                    isDisabled={isDisabled}
                                    handleRecordingStart={handleRecordingStart}
                                    handleRecordingStop={handleRecordingStop}
                                    currentlyRunningRecorderStep={currentlyRunningRecorderStep}
                                />
                            ) : null}
                        </animated.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AIPromptForm: FC<{
    isDisabled?: boolean;
    dispatch: Dispatch<any>;
    socket: Socket<DefaultEventsMap, DefaultEventsMap>;
    steps: Step[];
}> = ({ isDisabled, dispatch, socket, steps }) => {
    const [hash] = useState<string>(uuid());
    const [target, setTarget] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState<boolean>(false);

    const actualHash = uuidFromString(`${hash}${target}`);

    const handleGenerateSteps = () => {
        if (socket) {
            setIsGenerating(true);

            const hasHash = steps.some((step) => step.intentHash === actualHash);

            let stepsToSend = steps.filter((step) => step.kind !== 'separator');

            if (hasHash) {
                const lastIndexOfHash = stepsToSend.findLastIndex((step) => step.intentHash === actualHash);
                stepsToSend = stepsToSend.slice(0, lastIndexOfHash + 1);
            }

            socket.emit('stepDiscoveryRequest', {
                prompt: target,
                hash: actualHash,
                steps: stepsToSend,
            });
        }
    };

    useEffect(() => {
        if (socket) {
            socket.on('stepDiscoveryResponse', (data: { hash: string; prompt: string; steps: Step[] }) => {
                setIsGenerating(false);
                if (!data) {
                    return;
                }

                const hasHash = steps.some((step) => step.kind === 'separator' && step.intentHash === data.hash);
                if (!hasHash) {
                    dispatch({
                        type: 'add',
                        payload: createSeparatorStep(data.hash, data.prompt),
                    });
                    for (const step of data.steps) {
                        dispatch({
                            type: 'add',
                            payload: { ...step, intentHash: data.hash, intentPrompt: data.prompt },
                        });
                    }
                    dispatch({
                        type: 'add',
                        payload: createSeparatorStep(data.hash, data.prompt),
                    });
                } else {
                    const insertPosition = steps.findLastIndex((step) => step.intentHash === data.hash); // separator
                    for (const step of data.steps.reverse()) {
                        // we reverse them so that they are added in the correct order since the insert position doesn't change
                        dispatch({
                            type: 'addAtIndex',
                            payload: {
                                data: { ...step, intentHash: data.hash, intentPrompt: data.prompt },
                                index: insertPosition,
                            },
                        });
                    }
                }
            });
        }
        return () => {
            socket.off('stepDiscoveryResponse');
        };
    }, [socket, steps, dispatch, isGenerating]);

    return (
        <div>
            <div className="flex flex-col gap-2">
                <div className="text-lg pt-2">Instructions</div>
                <textarea
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    placeholder="Describe what you want the agent to do"
                    className="block w-full bg-slate-100 p-2"
                />
                <button
                    onClick={() => {
                        handleGenerateSteps();
                    }}
                    className="self-end bg-purple-600 text-white flex gap-x-2 px-2 py-1 rounded items-center disabled:bg-slate-100 disabled:text-slate-400"
                    disabled={isGenerating || isDisabled || target === ''}
                >
                    <Sparkles width="16px" height="16px" />
                    <span>Generate steps</span>
                    {isGenerating && <Spinner />}
                </button>
            </div>
        </div>
    );
};

const RecorderForm: FC<{
    isRecording: boolean;
    handleRecordingStart: (target: string) => void;
    handleRecordingStop: () => void;
    currentlyRunningRecorderStep: Step;
    isDisabled?: boolean;
}> = ({ isRecording, handleRecordingStart, handleRecordingStop, currentlyRunningRecorderStep, isDisabled }) => {
    const [target, setTarget] = useState<string>('');
    return (
        <div>
            {isRecording ? (
                <div>
                    <div className="bg-purple-100 py-3 px-4 rounded my-4">
                        <div className="flex items-center gap-x-2 mb-4">
                            <Circle className="text-purple-600" />
                            <div className="text-lg">Recording</div>
                            <button
                                onClick={handleRecordingStop}
                                className="ms-auto bg-purple-600 text-white flex gap-x-2 px-2 py-1 rounded items-center disabled:bg-slate-100 disabled:text-slate-400"
                            >
                                <CircleStop width="16px" height="16px" />
                                <span>Stop</span>
                            </button>
                        </div>
                        Use the browser to walk through the steps manually. Click stop recording when complete. You may
                        edit steps when you've completed the recording.
                    </div>
                    {currentlyRunningRecorderStep
                        ? (currentlyRunningRecorderStep.actions || []).map((action, index) => {
                              const Icon = actionToIcon[action.name as keyof typeof actionToIcon];
                              return (
                                  <div key={index} className="flex items-center gap-2 px-2 py-1 whitespace-nowrap">
                                      <Icon className="text-slate-500 shrink-0" width="24px" height="24px" />{' '}
                                      <span className="truncate py-1">
                                          {action.name}{' '}
                                          {action.value && <span className="border p-1 rounded">{action.value}</span>}
                                          <span className="text-slate-500 text-sm">
                                              {action.targetDescription ? ` on ${action.targetDescription}` : ''}
                                          </span>
                                      </span>
                                  </div>
                              );
                          })
                        : null}
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    <div className="text-lg pt-2">Instructions</div>
                    <textarea
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        placeholder="Describe the goal that the actions you're executing are expected to achieve"
                        className="block w-full bg-slate-100 p-2"
                    />
                    <button
                        onClick={() => {
                            handleRecordingStart(target);
                            setTarget('');
                        }}
                        className="self-end bg-purple-600 text-white flex gap-x-2 px-2 py-1 rounded items-center disabled:bg-slate-100 disabled:text-slate-400"
                        disabled={isDisabled || target === ''}
                    >
                        <Circle width="16px" height="16px" />
                        <span>Record</span>
                    </button>
                </div>
            )}
        </div>
    );
};
